import type { Node as PMNode, ResolvedPos, Slice } from "@tiptap/pm/model";
import { createProseMirrorMarkdownSerializer, MARKDOWN_SERIALIZER_PROFILES, serializeInlineToMarkdown } from "../../../src/shared/proseMirrorMarkdownSerializer";
import { isSpacerParagraph } from "../../../src/shared/proseMirrorNodePredicates";

/**
 * Markdown-flavored serializer for the text/plain clipboard flavor.
 *
 * Copying puts multiple representations on the clipboard at once; rich
 * targets (Word, Docs, Sudowrite itself) read text/html — which already
 * carries real <ul>/<li>/<h1> markup — while plain targets (LLM chat boxes,
 * plain editors) read text/plain. TipTap's default text/plain flattens list
 * items to bare lines and emits \n\n for EVERY block boundary including
 * blank spacer paragraphs (the "double line breaks become quadruple"
 * complaint). This serializer instead emits markdown: "- " / "1. " list
 * markers with two-space nesting, "#" headings, inline formatting, and exactly
 * one blank line between blocks — structure survives visually anywhere and
 * auto-parses in markdown-aware apps (ChatGPT, Notion, Slack…).
 */

const clipboardMarkdownSerializer = createProseMirrorMarkdownSerializer(MARKDOWN_SERIALIZER_PROFILES.CLIPBOARD);

const serializeInline = (node: PMNode): string => serializeInlineToMarkdown(clipboardMarkdownSerializer, node);

const isListNode = (node: PMNode): boolean =>
  node.type.name === "bulletList" || node.type.name === "orderedList" || node.type.name === "taskList";

const serializeList = (list: PMNode, depth: number): string => {
  const ordered = list.type.name === "orderedList";
  const indent = "  ".repeat(depth);
  const lines: string[] = [];
  let index = Number(list.attrs?.start ?? list.attrs?.order) || 1;

  list.forEach((item) => {
    const checkbox = item.type.name === "taskItem" ? `[${item.attrs?.checked ? "x" : " "}] ` : "";
    const marker = ordered ? `${index}. ` : `- ${checkbox}`;
    index += 1;
    const continuationIndent = indent + " ".repeat(marker.length);
    let firstBlockDone = false;
    let containsOnlyNestedLists = item.childCount > 0;

    item.forEach((child) => {
      if (!isListNode(child)) containsOnlyNestedLists = false;
    });

    // A copied selection can start inside a nested list, leaving an open
    // wrapper item with no selected parent text. Lift its children instead of
    // emitting orphaned indentation or inventing an empty parent bullet.
    if (containsOnlyNestedLists) {
      item.forEach((child) => {
        lines.push(serializeList(child, depth));
      });
      return;
    }

    item.forEach((child) => {
      if (isListNode(child)) {
        lines.push(serializeList(child, depth + 1));
        return;
      }
      const text = serializeInline(child).replace(/\n/g, `\n${continuationIndent}`);
      if (!firstBlockDone) {
        lines.push(`${indent}${marker}${text}`);
        firstBlockDone = true;
      } else if (text.trim()) {
        lines.push(`${continuationIndent}${text}`);
      }
    });

    // An entirely empty list item still gets its marker line
    if (!firstBlockDone && item.childCount === 0) {
      lines.push(`${indent}${marker}`);
    }
  });

  return lines.join("\n");
};

const reconstructSourceList = (slice: Slice, $from?: ResolvedPos): PMNode | null => {
  if (!$from || slice.content.childCount === 0) return null;

  let containsOnlyListItems = true;
  slice.content.forEach((node) => {
    if (node.type.name !== "listItem" && node.type.name !== "taskItem") containsOnlyListItems = false;
  });
  if (!containsOnlyListItems) return null;

  // openStart counts the wrappers omitted from the start of the slice. Moving
  // up by that amount finds the shared parent whose children are at the
  // fragment's top level (the source list for bare listItem fragments).
  const sourceDepth = $from.depth - slice.openStart;
  if (sourceDepth < 0) return null;
  const sourceList = $from.node(sourceDepth);
  if (!isListNode(sourceList)) return null;

  const attrs = { ...sourceList.attrs };
  if (sourceList.type.name === "orderedList") {
    const startAttr = Object.prototype.hasOwnProperty.call(attrs, "start") ? "start" : "order";
    const sourceStart = Number(attrs[startAttr]) || 1;
    attrs[startAttr] = sourceStart + $from.index(sourceDepth);
  }

  return sourceList.type.create(attrs, slice.content);
};

export const serializeSliceToMarkdown = (slice: Slice, $from?: ResolvedPos): string => {
  const sourceList = reconstructSourceList(slice, $from);
  if (sourceList) return serializeList(sourceList, 0);

  const blocks: string[] = [];

  slice.content.forEach((node) => {
    switch (node.type.name) {
      case "paragraph":
        // Spacer paragraphs are visual spacing, not content — skipping them is
        // what keeps one blank line between paragraphs instead of three.
        if (isSpacerParagraph(node)) return;
        {
          const text = serializeInline(node);
          if (text) blocks.push(text);
        }
        return;
      case "heading": {
        const level = Math.min(Math.max(Number(node.attrs?.level) || 1, 1), 6);
        blocks.push(`${"#".repeat(level)} ${serializeInline(node)}`);
        return;
      }
      case "bulletList":
      case "orderedList":
      case "taskList":
        blocks.push(serializeList(node, 0));
        return;
      default: {
        const text = node.isTextblock ? serializeInline(node) : node.textContent;
        if (text) blocks.push(text);
      }
    }
  });

  return blocks.join("\n\n");
};
